import { TypedDocumentNode, gql, useMutation } from '@apollo/client';

import { coinBalancesFragment } from '@sorare/core/src/contexts/currentUser/queries';
import {
  Level,
  useSnackNotificationContext,
} from '@sorare/core/src/contexts/snackNotification';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { formatGqlErrors } from '@sorare/core/src/gql';

import {
  BuyClubShopItemMutation,
  BuyClubShopItemMutationVariables,
} from './__generated__/useBuyShopItem.graphql';

const BUY_CLUB_SHOP_ITEM_MUTATION = gql`
  mutation BuyClubShopItemMutation($input: buyShopItemInput!, $sport: Sport!) {
    buyShopItem(input: $input) {
      currentUser {
        slug
        ...CurrentUserProvider_coinBalances
      }
      shopItem {
        ... on ShopItemInterface {
          id
          myPurchasesCount
          myLimitResetAt
          myBalance
          myAvailableUserShopItems {
            id
          }
        }
      }
      errors {
        path
        message
        code
      }
    }
  }
  ${coinBalancesFragment}
` as TypedDocumentNode<
  BuyClubShopItemMutation,
  BuyClubShopItemMutationVariables
>;

export const useBuyShopItem = () => {
  const [buyShopItem] = useMutation(BUY_CLUB_SHOP_ITEM_MUTATION);
  const { showNotification } = useSnackNotificationContext();
  const { sport } = useSportContext();

  return async (shopItemId: string) => {
    const result = await buyShopItem({
      variables: {
        input: {
          shopItemId,
        },
        sport,
      },
    });

    const errors = result.data?.buyShopItem?.errors || [];

    if (errors.length) {
      showNotification(
        'errors',
        { errors: formatGqlErrors(errors) },
        { level: Level.WARN }
      );
      return errors;
    }
    return null;
  };
};
